import { useState } from "react";
import { AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useSessions } from "@/hooks/useSessions";
import { useAuth } from "@/hooks/useAuth";
import { loginRedirectState } from "@/utils/auth";
import SessionList from "@/components/sessions/SessionList";
import SessionModal from "@/components/sessions/SessionModal";
import CreateAction from "@/components/common/CreateAction";

export default function SessionsPage() {
  const { t } = useTranslation("pages");
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const scope = location.pathname === "/sessions/all" ? "all" : "mine";
  const { data: sessions = [], isLoading, error } = useSessions(scope);
  const [searchParams, setSearchParams] = useSearchParams();
  const vehicleIdParam = searchParams.get("vehicleId") ?? "";

  const handleVehicleFilterChange = (vehicleId: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (vehicleId) {
      nextParams.set("vehicleId", vehicleId);
    } else {
      nextParams.delete("vehicleId");
    }
    setSearchParams(nextParams, { replace: true });
  };

  const handleCreateSession = () => {
    if (!user) {
      navigate("/login", { state: loginRedirectState(location) });
      return;
    }

    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
 <div className="page-container">
 <div className="flex justify-between items-center mb-3 sm:mb-8">
        <div>
 <h1 className="page-title">
            {t("sessions.pageTitle")}
          </h1>
 <p className="page-subtitle">{t("sessions.pageSubtitle")}</p>
        </div>
        <CreateAction
          onClick={handleCreateSession}
          label={t("sessions.logNewSession")}
        />
      </div>

      {error && (
 <div className="mb-3 sm:mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
 <div className="flex items-center">
 <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
 <p className="text-red-800">{t("sessions.errorLoadingSessions")}</p>
          </div>
        </div>
      )}

 <div className="flex gap-2 mb-3 sm:mb-6 border-b border-secondary-200">
        <NavLink
          to="/sessions/my"
 className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              isActive || location.pathname === "/sessions"
                ? "border-primary-600 text-primary-600 font-medium"
                : "border-transparent text-secondary-600 hover:text-secondary-900 hover:border-secondary-300"
            }`
          }
        >
          {t("sessions.tabs.mySessions")}
        </NavLink>
        <NavLink
          to="/sessions/all"
 className={({ isActive }) =>
            `flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
              isActive
                ? "border-primary-600 text-primary-600 font-medium"
                : "border-transparent text-secondary-600 hover:text-secondary-900 hover:border-secondary-300"
            }`
          }
        >
          {t("sessions.tabs.allSessions")}
        </NavLink>
      </div>

      <SessionList
        sessions={sessions}
        isLoading={isLoading}
        vehicleFilter={vehicleIdParam}
        onVehicleFilterChange={handleVehicleFilterChange}
        showOwner={scope === "all"}
        currentUserId={user?.id}
      />

      <SessionModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </div>
  );
}
